import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import EmptyState from '../../../commodity/components/ui/EmptyState';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { TeacherEvidenceRequest, getTeacherEvidenceRequests } from '../api/evidence-requests';
import { closingLabelInline } from '../vocabulary/learningVocabulary';

/**
 * What is missing, in the teacher's words, for each integrity reason code
 * (backend: reliability/domain/session-integrity-rules.ts). `completed_` and
 * `released_` prefixes share their meaning; an unknown code is shown as a
 * generic "revisar" rather than hidden.
 */
const REASON_COPY: Array<[RegExp, string]> = [
  [
    /_with_missing_evidence$/,
    'Falta evidencia obligatoria de la sesión (rúbrica, notas o grabación).',
  ],
  [/_without_booking$/, 'La sesión no tiene una reserva confirmada.'],
  [/_on_unconfirmed_booking$/, 'La reserva de la sesión no está confirmada.'],
  [/^recording_failed$/, 'La grabación de la sesión falló.'],
  [/^recording_not_finalised$/, 'La grabación de la sesión no terminó de procesarse.'],
  [/^recording_ready_without_reference$/, 'La grabación figura lista pero no tiene archivo.'],
  [/^released_/, 'El pago se liberó antes de que la sesión quedara completa.'],
  [/rubric_changed_after_release$/, 'La rúbrica se cambió después de liberar el pago.'],
  [
    /^enrollment_completed_with_open_milestones$/,
    'El curso figura terminado con sesiones abiertas.',
  ],
  [/^final_completion_stamp_without_final_milestone$/, `Falta ${closingLabelInline()}.`],
];

export const reasonCopy = (reason: string): string =>
  REASON_COPY.find(([pattern]) => pattern.test(reason))?.[1] ??
  'Hay algo que revisar en la evidencia de esta sesión.';

const fmtDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const card: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 10,
  padding: 14,
  background: '#fff',
};

/**
 * ADM-03-FR-06 — evidence requests addressed to the teacher, in the console.
 *
 * In-app, not email (Sprint 3 COPY rule). Each request names the session, what
 * the integrity check found missing, and the deadline the server set; the
 * link goes to that session's evidence screen, where the teacher fixes it. The
 * request closes when the next integrity run finds the evidence — nothing on
 * this screen marks it done.
 */
const TeacherEvidenceRequests: React.FC = () => {
  const [requests, setRequests] = useState<TeacherEvidenceRequest[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setError(null);
    setRequests(null);
    try {
      const res = await getTeacherEvidenceRequests();
      setRequests(res?.requests ?? []);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ marginBottom: 4 }}>Evidencia pendiente</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Sesiones en las que el equipo de IQMaxx te pidió completar la evidencia, y hasta cuándo
        tienes para hacerlo.
      </p>

      {error ? (
        <ErrorState action="cargar tus solicitudes de evidencia" error={error} onRetry={load} />
      ) : requests === null ? (
        <LoadingState what="tus solicitudes de evidencia" />
      ) : requests.length === 0 ? (
        <EmptyState
          message="No tienes solicitudes de evidencia pendientes."
          detail="Si falta algo en una sesión, aparecerá aquí con su plazo."
        />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {requests.map((r) => {
            const titleId = `evidence-request-${r.milestoneId}`;
            const expired = r.deadlineState === 'EXPIRED';
            return (
              <li key={`${r.enrollmentId}#${r.milestoneId}`} style={card} aria-labelledby={titleId}>
                <div id={titleId} style={{ fontWeight: 700 }}>
                  {r.sequence ? `Sesión ${r.sequence}` : 'Inscripción'} · {r.enrollmentId}
                </div>
                <ul style={{ margin: '8px 0', paddingLeft: 18, fontSize: 14 }}>
                  {r.reasons.map((reason) => (
                    <li key={reason}>{reasonCopy(reason)}</li>
                  ))}
                </ul>
                <p style={{ margin: '0 0 8px', fontSize: 14, color: expired ? '#8f2323' : '#444' }}>
                  {expired
                    ? `El plazo venció el ${fmtDate(
                        r.dueAt,
                      )}. El equipo de IQMaxx revisará el caso.`
                    : `Plazo: ${fmtDate(r.dueAt)}`}
                </p>
                {r.sequence ? (
                  <Link
                    to={`/enrollments/${encodeURIComponent(r.enrollmentId)}/sessions/${r.sequence}`}
                  >
                    Completar la evidencia de la sesión {r.sequence}
                  </Link>
                ) : (
                  <Link to={`/enrollments/${encodeURIComponent(r.enrollmentId)}`}>
                    Ver la inscripción
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TeacherEvidenceRequests;
